import type {
  ActivityIngredientAdapter,
  DateOnlyString,
  HeartedHotel,
  HeartedRoom,
  HotelIngredientAdapter,
  ItinerarySearchCriteria,
  OwnStayIngredientAdapter,
  OwnTransportIngredientAdapter,
  OwnTransportType,
  RecipeMetaData,
  TransportIngredientAdapter,
} from '@libs/trip-planner-interfaces';
import {
  TransportVehicleType,
  GraphQL,
  IngredientType,
  AvailabilityOrder,
  TransportType,
} from '@libs/trip-planner-interfaces';
import {
  assertValue,
  exhaustiveGuard,
  isDefined,
  normalizeDateOnlyString,
} from '@libs/utils';
import {
  getSmartSearchSerializedFeatureToggles,
  isFeatureToggleActive,
} from '@libs/feature-toggle';

import { getMarketOrigin, setSessionId } from '../../utils';

import { getInputPaxGroupQL } from './inputPaxGroupQL';

export function getRecipeQL(
  criteria: ItinerarySearchCriteria,
  isSharedWidgetRecipe?: boolean
): GraphQL.RecipeQL {
  setSessionId(criteria);

  const ingredients = criteria.userCriteria.ingredients;
  assertValue(ingredients, 'search-service-smart-search > utils > getRecipeQL');

  return {
    pax: getInputPaxGroupQL(criteria.userCriteria.pax),
    ingredients: ingredients.map((ingredient) =>
      getIngredientQL(
        ingredient,
        isSharedWidgetRecipe,
        criteria.numberOfHotelSuggestions
      )
    ),
    market: getMarketOrigin(),
    startDateOnly: normalizeDateOnlyString(
      criteria.userCriteria.startSearchDate
    ),
    endDateOnly: normalizeDateOnlyString(criteria.userCriteria.endSearchDate),
    selectedDate: normalizeDateOnlyString(criteria.userCriteria.selectedDate),
    affiliateId: criteria.businessCriteria.affiliateId,
    featureToggles: getSmartSearchSerializedFeatureToggles(),
    skipCache:
      criteria.userCriteria.skipCache ||
      isFeatureToggleActive('skip-smart-search-cache'),
    returnEmptyIngredientResults: true,
    sessionId: criteria.userCriteria.sessionId,
    metadata: getMetadataRequestQL(criteria.businessCriteria.metaData),
    searchForPackageRates: criteria.businessCriteria.searchForPackageRates,
    bypassRoundtripLogic: isFeatureToggleActive('bypass-roundtrip'),
    dontGroupByFlightNumbers: true,
    avoidDateShift: criteria.businessCriteria.avoidDateShift,
    searchOnlyLiveSelectedDay:
      criteria.businessCriteria.searchOnlyLiveSelectedDay,
  };
}

function getMetadataRequestQL(
  metaData?: RecipeMetaData
): GraphQL.MetadataRequestQL {
  return {
    sourceType: GraphQL.InputSourceTypeQL.Planner,
    generatedFromAI: metaData?.generatedFromAI,
    salesChannel: metaData?.salesChannel,
  };
}

const NUMBER_OF_HOTEL_SUGGESTIONS = 15;

export function getIngredientQL(
  ingredient:
    | OwnStayIngredientAdapter
    | HotelIngredientAdapter
    | TransportIngredientAdapter
    | ActivityIngredientAdapter
    | OwnTransportIngredientAdapter,
  isSharedWidgetRecipe?: boolean,
  numberOfHotelSuggestions = NUMBER_OF_HOTEL_SUGGESTIONS
): GraphQL.IngredientQL {
  switch (ingredient.ingredientType) {
    case IngredientType.OwnStay: {
      return {
        ...ingredient.constraint,
        ingredientType: GraphQL.InputIngredientTypeQL.OwnStay,
        numberOfNights: ingredient.numberOfNights,
        subIngredients: ingredient.subIngredients.map((subIngredient) =>
          getIngredientQL(subIngredient, undefined, numberOfHotelSuggestions)
        ),
      };
    }

    case IngredientType.OwnTransport: {
      return {
        ingredientType: GraphQL.InputIngredientTypeQL.OwnTransport,
        ownTransportType: getOwnTransportTypeQL(ingredient.ownTransportType),
      };
    }

    case IngredientType.Hotel: {
      return {
        ...ingredient.constraint,
        boardBasis: ingredient.constraint.boardBasis,
        selectedHotelTypes: ingredient.constraint.selectedHotelTypes,
        ingredientType: GraphQL.InputIngredientTypeQL.Hotel,
        numberOfNights: ingredient.numberOfNights,
        numberOfSuggestions: numberOfHotelSuggestions,
        priceFilters: ingredient.priceFilters,
        heartedHotels: ingredient.heartedHotels.map(getHeartedHotelQL),
        subIngredients: ingredient.subIngredients.map((subIngredient) =>
          getIngredientQL(subIngredient, undefined, numberOfHotelSuggestions)
        ),
        suggestedBy: getInputAvailibityOrderQL(ingredient.suggestedBy),
        polygonId:
          isSharedWidgetRecipe || !ingredient.constraint.polygonIds?.length
            ? ingredient.constraint.polygonId
            : undefined,
        includeAlternativeResults: true,
      };
    }

    case IngredientType.Activity: {
      return {
        ingredientType: GraphQL.InputIngredientTypeQL.Ticket,
        code: ingredient.code,
        offset: ingredient.offset,
        tickets: ingredient.tickets,
        includeAlternativeResults: true,
        selectedTicketDate: ingredient.selectedTicketDate as
          | DateOnlyString
          | undefined,
      };
    }

    case IngredientType.Flight: {
      return {
        transportTypes: [GraphQL.InputTransportTypeQL.Flight],
        ingredientType: GraphQL.InputIngredientTypeQL.Transport,
        fromIata: ingredient.fromPort,
        toIata: ingredient.toPort,
        flightNumbers: ingredient.transportIds,
        maxNumberOfStops: ingredient.maxNumberOfStops,
        departureTimeFilters: ingredient.departureTimeFilters,
        arrivalTimeFilters: ingredient.arrivalTimeFilters,
        maxDurationInHours: ingredient.maxDurationInHours,
        priceFilters: ingredient.priceFilters,
        carriers: ingredient.carrierFilters,
        suggestedBy: getInputAvailibityOrderQL(ingredient.suggestedBy),
        includeAlternativeResults: true,
      };
    }

    case IngredientType.Ferry: {
      return {
        transportTypes: [GraphQL.InputTransportTypeQL.Ferry],
        ingredientType: GraphQL.InputIngredientTypeQL.Transport,
        fromIata: ingredient.fromPort,
        toIata: ingredient.toPort,
        flightNumbers: ingredient.transportIds,
        departureTimeFilters: ingredient.departureTimeFilters,
        arrivalTimeFilters: ingredient.arrivalTimeFilters,
        maxDurationInHours: ingredient.maxDurationInHours,
        priceFilters: ingredient.priceFilters,
        carriers: ingredient.carrierFilters,
        vehicles: ingredient.vehicles.map((vehicle) =>
          getInputTransportVehicleTypeQL(vehicle)
        ),
        suggestedBy: getInputAvailibityOrderQL(ingredient.suggestedBy),
        includeAlternativeResults: true,
      };
    }

    default:
      exhaustiveGuard(ingredient, 'Ingredient adapter not handled');
  }
}

function getHeartedHotelQL(heartedHotel: HeartedHotel): GraphQL.HeartedHotelQL {
  const heartedHotelQL: GraphQL.HeartedHotelQL = {
    code: heartedHotel.code,
    isRefundable: heartedHotel.isRefundable,
    roomCodes: heartedHotel.roomCodes,
    rooms: heartedHotel.rooms?.map(getHeartedRoomQL),
  };

  if (heartedHotelQL.rooms && heartedHotelQL.rooms.length) {
    delete heartedHotelQL.roomCodes;
  }

  return heartedHotelQL;
}

function getHeartedRoomQL(heartedRoom: HeartedRoom): GraphQL.HeartedRoomQL {
  const heartedRoomQL: GraphQL.HeartedRoomQL = {
    boardBasis: heartedRoom.boardBasis,
    code: heartedRoom.code,
    roomTypeId: isFeatureToggleActive('room-types')
      ? heartedRoom.roomTypeId
      : undefined,
  };

  if (isDefined(heartedRoomQL.roomTypeId)) {
    delete heartedRoomQL.code;
  }

  return heartedRoomQL;
}

function getInputAvailibityOrderQL(
  availabilityOrder: AvailabilityOrder = AvailabilityOrder.None
): GraphQL.InputAvailibityOrderQL {
  switch (availabilityOrder) {
    case AvailabilityOrder.None:
      return GraphQL.InputAvailibityOrderQL.None;

    case AvailabilityOrder.Cheapest:
      return GraphQL.InputAvailibityOrderQL.Cheapest;

    case AvailabilityOrder.Popular:
      return GraphQL.InputAvailibityOrderQL.Popular;

    default:
      exhaustiveGuard(availabilityOrder, 'Unhandled availability order');
  }
}

function getInputTransportVehicleTypeQL(
  vehicle: TransportVehicleType
): GraphQL.InputTransportVehicleTypeQL {
  switch (vehicle) {
    case TransportVehicleType.None:
      return GraphQL.InputTransportVehicleTypeQL.None;

    case TransportVehicleType.Bicycle:
      return GraphQL.InputTransportVehicleTypeQL.Bicycle;

    case TransportVehicleType.MiniBus4mHigh6mLong:
      return GraphQL.InputTransportVehicleTypeQL.MiniBus_4MHigh_6MLong;

    case TransportVehicleType.MiniBus8mLong:
      return GraphQL.InputTransportVehicleTypeQL.MiniBus_8MLong;

    case TransportVehicleType.MotorBike:
      return GraphQL.InputTransportVehicleTypeQL.MotorBike;

    case TransportVehicleType.MultiPurposeVehicle:
      return GraphQL.InputTransportVehicleTypeQL.MultiPurposeVehicle;

    case TransportVehicleType.Vehicle4m70cmLong:
      return GraphQL.InputTransportVehicleTypeQL.Vehicle_4M_70CmLong;

    case TransportVehicleType.Vehicle6mLong:
      return GraphQL.InputTransportVehicleTypeQL.Vehicle_6MLong;

    default:
      exhaustiveGuard(vehicle, 'Unhandled vehicle type');
  }
}

function getOwnTransportTypeQL(
  ownTransportType: OwnTransportType
): GraphQL.OwnTransportTypeQL {
  switch (ownTransportType) {
    case TransportType.Car:
      return GraphQL.OwnTransportTypeQL.Car;
    case TransportType.Bicycle:
      return GraphQL.OwnTransportTypeQL.Cycle;
    case TransportType.Walk:
      return GraphQL.OwnTransportTypeQL.Walk;
    case TransportType.None:
      return GraphQL.OwnTransportTypeQL.Unknown;

    default:
      throw new Error('Unknown transport type');
  }
}
